import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import config from 'utils/config';
import { FIND_VOUCHER_BY_REFERENCE } from 'store/queries';
import SearchDiscountCodeByCodeForm from './SearchDiscountCodeByCodeForm';
import { StyledManageVoucherContainer, StyledSearchByHeader, StyledSearchSuggestionsList } from './ManageDiscountCode.styles';
import { VOUCHER_TYPE_DISCOUNT } from '../../constants/vouchers';
import { SEARCH_DISCOUNT_CODES } from '../../store/queries';
import SearchDiscountCodeByDateForm from './SearchDiscountCodeByDateForm';
import { Column } from '../../components/shared/Layout';

const isSearchDiscountByDateAvailable = config('ENABLE_SEARCH_DISCOUNT_BY_DATE', { canOverride: true }) === 'true';

const ERROR_TYPE = {
  SEARCH_BY_CODE_ERROR: 'searchByCodeError',
  SEARCH_BY_DATE_ERROR: 'searchByDateError'
};

const ManageDiscountCode = ({ history }) => {
  const [discountCode, setDiscountCode] = useState('');
  const [validTravelDate, setValidTravelDate] = useState(null);
  const [validSalesDate, setValidSalesDate] = useState(null);
  const [errorType, setErrorType] = useState('');

  const [findVoucherByReference, { loading: findByRefLoading, data: findByRefData, error: findByRefError }] =
    useLazyQuery(FIND_VOUCHER_BY_REFERENCE);
  const [searchDiscountCodes, { loading: searchLoading, data: searchData, error: searchError }] = useLazyQuery(SEARCH_DISCOUNT_CODES);

  const hasError = findByRefError || searchError || searchData?.searchDiscountCodes?.length === 0;
  const loading = findByRefLoading || searchLoading;

  if (findByRefData && findByRefData.findVoucherByReferenceAndMarket) {
    const { alias } = findByRefData.findVoucherByReferenceAndMarket;
    history.push(`/discountCodes/${alias}`);

    return null;
  }
  if (searchData && searchData.searchDiscountCodes) {
    if (searchData.searchDiscountCodes.length === 1) {
      history.push(`/discountCodes/${searchData.searchDiscountCodes[0].alias}`);

      return null;
    }
    if (searchData.searchDiscountCodes.length > 1) {
      history.push(`/discountCodes/search?validTravelDate=${validTravelDate}&validSalesDate=${validSalesDate}`);

      return null;
    }
  }

  return (
    <StyledManageVoucherContainer>
      <h1>
        <FormattedMessage id="discountCodes.manageDiscountCode" />
      </h1>
      {loading && (
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      )}
      {!loading && (
        <>
          {hasError && (
            <div data-cy="discount-code-not-found">
              {errorType === ERROR_TYPE.SEARCH_BY_CODE_ERROR && (
                <p>
                  <FormattedMessage id="discountCodes.notFoundByCode" data-cy="discount-code-not-found-by-code" />:{' '}
                  <strong>{discountCode}</strong>
                </p>
              )}
              {errorType === ERROR_TYPE.SEARCH_BY_DATE_ERROR && (
                <p>
                  <FormattedMessage id="discountCodes.notFoundByDate1" data-cy="discount-code-not-found-by-date1" />:{' '}
                  <strong>{validSalesDate || '-'}</strong>
                  <FormattedMessage id="discountCodes.notFoundByDate2" data-cy="discount-code-not-found-by-date2" />:{' '}
                  <strong>{validTravelDate || '-'}</strong>
                </p>
              )}
              <p>
                <strong>
                  <FormattedMessage id="vouchers.searchSuggestionsTitle" />
                </strong>
                :
              </p>
              <StyledSearchSuggestionsList>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionSpelling" />
                </li>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionsAlternative" />
                </li>
              </StyledSearchSuggestionsList>
            </div>
          )}
          <StyledSearchByHeader>
            <FormattedMessage id={hasError ? 'vouchers.searchAgain' : 'vouchers.searchBy'} />:
          </StyledSearchByHeader>
          <SearchDiscountCodeByCodeForm
            onSubmitSearch={(values) => {
              setDiscountCode(values.discountCode);
              setErrorType(ERROR_TYPE.SEARCH_BY_CODE_ERROR);

              return findVoucherByReference({
                variables: {
                  reference: values.discountCode,
                  voucherType: VOUCHER_TYPE_DISCOUNT
                }
              });
            }}
          />
          {isSearchDiscountByDateAvailable && (
            <SearchDiscountCodeByDateForm
              onSubmitSearch={(values) => {
                const travelDate = values.validTravelDate;
                const salesDate = values.validSalesDate;

                setValidTravelDate(travelDate);
                setValidSalesDate(salesDate);

                setErrorType(ERROR_TYPE.SEARCH_BY_DATE_ERROR);

                return searchDiscountCodes({
                  variables: {
                    validTravelDate: travelDate,
                    validSalesDate: salesDate
                  }
                });
              }}
            />
          )}
        </>
      )}
    </StyledManageVoucherContainer>
  );
};

ManageDiscountCode.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired
};
export default withRouter(ManageDiscountCode);
